import { Pipe, PipeTransform } from '@angular/core';
import { DateTime } from 'luxon';
import { TimeZoneRetrieval } from './Time-Zone-Retrieval';

/**
 * FormattedTimePipe
 *
 * IMPORTANT: This pipe uses browser timezone by default.
 * For store-related times (opening hours, orders, reservations), use StoreDatePipe instead,
 * which ensures times are displayed in the STORE'S timezone, not the browser's.
 *
 * Only use this pipe for timezone-independent data like Date of Birth.
 */
@Pipe({
  name: 'formattedTime',
})
export class FormattedTimePipe implements PipeTransform {
  private timeZoneRetrieval = new TimeZoneRetrieval();

  transform(
    value: Date | string | number,
    timeFormat: string = 'TIME_FORMAT_24H',
    locale: string = 'en-US',
    timezone: string = this.timeZoneRetrieval.retrieveTimeZone(),
  ): string {
    if (!value) return '';

    try {
      let dateTime: DateTime;

      if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
        // Handle simple time format (HH:mm)
        const [hours, minutes] = value.split(':').map(Number);
        dateTime = DateTime.fromObject(
          { hour: hours, minute: minutes },
          { zone: timezone },
        );
      } else {
        // Handle ISO string or Date object
        dateTime = DateTime.fromISO(value.toString(), { zone: 'utc' }).setZone(
          timezone,
        );
      }

      if (!dateTime.isValid) {
        console.error('Invalid date:', value);
        return '';
      }

      const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: timeFormat === 'TIME_FORMAT_12H',
        timeZone: timezone,
      };

      return new Intl.DateTimeFormat(locale, options).format(
        dateTime.toJSDate(),
      );
    } catch (error) {
      console.error('Error formatting time:', error);
      return value.toString();
    }
  }
}
